import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import PostcardOptionsMenu from '../Menus/PostcardOptionsMenu';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { IoPaperPlaneOutline } from 'react-icons/io5';
import BookmarkIcon from '../Icons/BookmarkIcon';
import { AiOutlineEye } from 'react-icons/ai';
import { FiMessageCircle } from 'react-icons/fi';
import fetchClient from '../../utils/fetchClient';
import { BiInfoCircle } from 'react-icons/bi';
import { getThumbnailUrl, isGifUrl, isVideoUrl } from '../../utils/imagePreview';
import { FaPlay } from 'react-icons/fa';
import GifIcon from '../Icons/GifIcon';

const ChannelPostcardCard = ({ postcard, imageUrl, onImageClick, isGallery = false, className = '', onDeleted, imgRef }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const showDelete = user && postcard && user.id === postcard.user_id;
    
    // Единая логика выбора превью: сначала явное preview_78x101_url, иначе строим по шаблону из image_url
    const optimizedImageUrl = getThumbnailUrl(postcard, imageUrl, '78x101');
    const originalUrlForBadges = postcard?.image_url || imageUrl || optimizedImageUrl;
    
    const handleMenuSelect = async (action) => {
        setMenuOpen(false);
        if (action === 'deletePostcard') {
            try {
                await fetchClient.delete(`/image_relations/${postcard.id}`);
                if (typeof onDeleted === 'function') {
                    onDeleted(postcard.id);
                }
            } catch (e) {
                alert(e.message || 'Ошибка удаления открытки');
            }
        }
        // Здесь можно добавить обработку других действий
    };
    const handleImageClick = (e) => {

        if (onImageClick) {
            onImageClick(e);
        }
    };
    // Статистика открытки (заглушка, если нет данных)
    const stats = postcard?.stats || { views: 0, reposts: 0, comments: 0, bookmarks: 0 };
    
    // Если это галерея - показываем упрощенную версию
    if (isGallery) {
        return (
            <div className={`group relative rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 shadow-sm transition-all hover:shadow-md border-gray-200 dark:border-gray-900 mx-auto w-full aspect-[320/410] overflow-hidden ${className}`}>
                {/* Мягкие индикаторы для gif и видео в нижнем правом углу */}
                {originalUrlForBadges && (
                    isGifUrl(originalUrlForBadges) ? (
                        <div className="absolute bottom-1.5 right-1.5 z-20 w-6 h-6 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center shadow-sm pointer-events-none">
                            <GifIcon className="text-white text-xs" />
                        </div>
                    ) : isVideoUrl(originalUrlForBadges) ? (
                        <div className="absolute bottom-1.5 right-1.5 z-20 w-6 h-6 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center shadow-sm pointer-events-none">
                            <FaPlay className="text-white text-xs" />
                        </div>
                    ) : null
                )}
                
                {/* Превью изображения или видео */}
                {optimizedImageUrl && (
                    isVideoUrl(optimizedImageUrl) ? (
                        <video
                            src={optimizedImageUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover cursor-pointer"
                            style={{ display: 'block' }}
                            onClick={handleImageClick}
                        />
                    ) : (
                        <img
                            ref={imgRef}
                            src={optimizedImageUrl}
                            alt={postcard.title || postcard.description || 'Открытка'}
                            className="w-full h-full object-cover cursor-pointer"
                            style={{ display: 'block' }}
                            onClick={handleImageClick}
                            onError={(e) => {
                                // Если изображение не загрузилось с crossOrigin, пробуем без него
                                if (e.target.crossOrigin) {
                                    console.log('🔄 Retrying image without crossOrigin:', optimizedImageUrl);
                                    e.target.crossOrigin = null;
                                    e.target.src = optimizedImageUrl;
                                }
                            }}
                        />
                    )
                )}
            </div>
        );
    }
    
    return (
        <div className={`group relative rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 shadow-sm transition-all hover:shadow-md border-gray-200 dark:border-gray-900 mx-auto w-full aspect-[320/410] overflow-hidden ${className}`}>
            {/* Мягкие индикаторы для gif и видео в нижнем правом углу */}
            {optimizedImageUrl && (
                isGifUrl(optimizedImageUrl) ? (
                    <div className="absolute bottom-1.5 right-1.5 z-20 w-6 h-6 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center shadow-sm pointer-events-none">
                        <GifIcon className="text-white text-xs" />
                    </div>
                ) : isVideoUrl(optimizedImageUrl) ? (
                    <div className="absolute bottom-1.5 right-1.5 z-20 w-6 h-6 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center shadow-sm pointer-events-none">
                        <FaPlay className="text-white text-xs" />
                    </div>
                ) : null
            )}
            
            {/* Индикатор статуса модерации */}
            <div className="absolute top-2 right-2 z-20">
                {postcard.is_banned ? (
                    <div 
                        className="px-2 py-0.5 rounded bg-red-500 text-white text-xs font-bold shadow text-center cursor-help" 
                        title="Открытка забанена модератором"
                    >🚫</div>
                ) : postcard.is_approved ? (
                    <div 
                        className="px-2 py-0.5 rounded bg-green-500 text-white text-xs font-bold shadow text-center cursor-help" 
                        title="Открытка одобрена модератором"
                    >✅</div>
                ) : (
                    <div 
                        className="px-2 py-0.5 rounded bg-yellow-500 text-white text-xs font-bold shadow text-center cursor-help" 
                        title="Открытка ожидает модерации"
                    >⏳</div>
                )}
            </div>
            
            {/* Кнопка действий */}
            <div className="absolute top-16 right-2 bg-transparent p-2 z-20 flex flex-col items-center gap-3">
                <button
                    className="bg-white dark:bg-black bg-opacity-50 dark:bg-opacity-50 text-black dark:text-white hover:bg-opacity-80 dark:hover:bg-opacity-80 p-3 rounded-full transition-all ease-in-out backdrop-blur-sm"
                    onClick={() => setMenuOpen((v) => !v)}
                >
                    <MoreVertical className="h-4 w-4" />
                </button>
                <PostcardOptionsMenu
                    isOpen={menuOpen}
                    onSelect={handleMenuSelect}
                    onClose={() => setMenuOpen(false)}
                    showDelete={showDelete}
                />
            </div>
            {/* Превью изображения или видео */}
            {optimizedImageUrl && (
                isVideoUrl(optimizedImageUrl) ? (
                    <video
                        src={optimizedImageUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover cursor-pointer"
                        style={{ display: 'block' }}
                        onClick={handleImageClick}
                    />
                ) : (
                    <img
                        ref={imgRef}
                        src={optimizedImageUrl}
                        alt={postcard.title || postcard.description || 'Открытка'}
                        className="w-full h-full object-cover cursor-pointer"
                        style={{ display: 'block' }}
                        onClick={handleImageClick}
                        onError={(e) => {
                            // Если изображение не загрузилось с crossOrigin, пробуем без него
                            if (e.target.crossOrigin) {
                                console.log('🔄 Retrying image without crossOrigin:', optimizedImageUrl);
                                e.target.crossOrigin = null;
                                e.target.src = optimizedImageUrl;
                            }
                        }}
                    />
                )
            )}
            {/* Статистика */}
            <div className="absolute bottom-2 left-2 p-2 bg-white dark:bg-black bg-opacity-40 dark:bg-opacity-40 flex gap-3 z-10 rounded-xl max-w-max px-3 py-2 backdrop-blur-sm">
                <div className="flex items-center gap-2 text-black dark:text-white text-xs">
                    <AiOutlineEye />
                    <span>{stats.views}</span>
                </div>
                <div className="flex items-center gap-2 text-black dark:text-white text-xs">
                    <IoPaperPlaneOutline />
                    <span>{stats.reposts}</span>
                </div>
                <div className="flex items-center gap-2 text-black dark:text-white text-xs">
                    <BookmarkIcon size={16} />
                    <span>{stats.bookmarks}</span>
                </div>
            </div>
            {/* Кнопки действий (Отправить) */}
            <div className="absolute bottom-10 right-2 bg-transparent p-2 z-30 flex flex-col items-center gap-3">
                <button 
                    onClick={() => navigate(`/mychannels/${postcard.collection_id}/${postcard.id}/create-congratulation`)}
                    className="bg-white dark:bg-black bg-opacity-50 dark:bg-opacity-50 text-black dark:text-white hover:bg-opacity-80 dark:hover:bg-opacity-80 p-3 rounded-full transition-all ease-in-out backdrop-blur-sm"
                    title="Создать поздравление"
                >
                    <IoPaperPlaneOutline className="text-2xl" />
                </button>
            </div>
        </div>
    );
};

export default ChannelPostcardCard; 